import { z } from 'zod'

const MAX_COMPONENT_WEIGHT_GRAMS = 1_000_000

function formatWeightToken(value: number): string {
  if (!Number.isFinite(value)) {
    return ''
  }
  if (Number.isInteger(value)) {
    return String(value)
  }
  const text = value.toFixed(4)
  return text.replace(/\.?0+$/, '')
}

/** Stable string for controlled text inputs from stored gram values. */
export function formatComponentWeightForFormInput(value: number): string {
  if (!Number.isFinite(value) || value <= 0) {
    return ''
  }
  return formatWeightToken(value)
}

/** Display weight in the components table (column header already says grams). */
export function formatComponentWeightGramsDisplay(value: number): string {
  if (!Number.isFinite(value) || value <= 0) {
    return '—'
  }
  return formatWeightToken(value)
}

/**
 * Parses a weight field (grams). Allows optional `g` suffix and `,` as decimal separator.
 * Returns null if empty/invalid/out of range.
 */
export function parseComponentWeightGramsField(raw: string): number | null {
  let compact = raw.trim().replace(/\s/g, '').replace(/g$/i, '')
  if (compact.length === 0) {
    return null
  }

  compact = compact.replace(',', '.')
  if (compact.startsWith('.')) {
    compact = `0${compact}`
  }
  if (/^\d+\.$/.test(compact)) {
    compact = compact.slice(0, -1)
  }
  if (!/^\d+(\.\d+)?$/.test(compact)) {
    return null
  }

  const value = Number.parseFloat(compact)
  if (!Number.isFinite(value) || value <= 0 || value > MAX_COMPONENT_WEIGHT_GRAMS) {
    return null
  }

  return value
}

export const componentWeightGramsInputSchema = z
  .string()
  .trim()
  .min(1, 'required')
  .transform((value, context) => {
    const parsed = parseComponentWeightGramsField(value)
    if (parsed === null) {
      context.addIssue({
        code: 'custom',
        message: 'invalid',
      })
      return z.NEVER
    }
    return parsed
  })

export type ComponentWeightGramsInputError = 'required' | 'invalid'

export function getComponentWeightGramsInputError(
  raw: string
): ComponentWeightGramsInputError | null {
  const trimmed = raw.trim()
  if (trimmed.length === 0) {
    return 'required'
  }
  if (parseComponentWeightGramsField(trimmed) === null) {
    return 'invalid'
  }
  return null
}
